import type { NHovBranchId } from "../types";
import { NHOV_BRANCH_IDS } from "./nHovBranches";

export interface NHovCandidateTheme {
  readonly hue: number;
}

export interface NHovCandidateOffer {
  readonly themesByBranchId: Readonly<Record<NHovBranchId, NHovCandidateTheme>>;
}

export const NHOV_THEME_HUE_MIN = 140;
export const NHOV_THEME_HUE_MAX = 250;
export const FIRST_NHOV_THEME_HUE_MIN = 140;
export const FIRST_NHOV_THEME_HUE_MAX = 165;
export const NHOV_CANDIDATE_MIN_HUE_DISTANCE = 16;
export const HOV_THEME_END_HUE = 255;

const NORMAL_OFFER_CANDIDATE_COUNT = NHOV_BRANCH_IDS.length;
const NORMAL_HUE_JITTER_DEGREES = 8;

export function isNHovThemeHue(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= NHOV_THEME_HUE_MIN &&
    value <= NHOV_THEME_HUE_MAX
  );
}

export function normalizeNHovThemeHue(value: unknown): number | null {
  if (!isNHovThemeHue(value)) return null;
  return roundHue(value);
}

export function resolveNHovCandidateOffer(
  nHovCount: number,
): NHovCandidateOffer {
  const safeCount = normalizeNHovCount(nHovCount);

  if (safeCount === 0) {
    const theme = {
      hue: mapHashToHue(
        hash32(0x6d2b79f5),
        FIRST_NHOV_THEME_HUE_MIN,
        FIRST_NHOV_THEME_HUE_MAX,
      ),
    };
    return {
      themesByBranchId: {
        hgAmplify: theme,
        hgDiscount: theme,
        hngScale: theme,
        hgNHovEffectsToHng: theme,
      },
    };
  }

  const seed = hash32(safeCount ^ 0x9e3779b9);
  const hues = buildNormalOfferHues(seed);
  const shuffledHueIndices = shuffleIndices(
    NORMAL_OFFER_CANDIDATE_COUNT,
    seed ^ 0x85ebca6b,
  );
  const themes: Record<NHovBranchId, NHovCandidateTheme> = {
    hgAmplify: { hue: NHOV_THEME_HUE_MIN },
    hgDiscount: { hue: NHOV_THEME_HUE_MIN },
    hngScale: { hue: NHOV_THEME_HUE_MIN },
    hgNHovEffectsToHng: { hue: NHOV_THEME_HUE_MIN },
  };

  NHOV_BRANCH_IDS.forEach((branchId, index) => {
    themes[branchId] = { hue: hues[shuffledHueIndices[index]] };
  });

  return { themesByBranchId: themes };
}

function normalizeNHovCount(nHovCount: number): number {
  if (!Number.isFinite(nHovCount) || nHovCount <= 0) return 0;
  return Math.floor(nHovCount);
}

function buildNormalOfferHues(seed: number): readonly number[] {
  const span = NHOV_THEME_HUE_MAX - NHOV_THEME_HUE_MIN;
  const step = span / (NORMAL_OFFER_CANDIDATE_COUNT - 1);
  // One hue per wide lane keeps the four offers visibly separated; the small
  // deterministic jitter makes each NHov count feel distinct.
  return Array.from({ length: NORMAL_OFFER_CANDIDATE_COUNT }, (_, index) => {
    const baseHue = NHOV_THEME_HUE_MIN + step * index;
    const jitterUnit = hashToUnit(hash32(seed + index * 0x27d4eb2d));
    const jitter = (jitterUnit * 2 - 1) * NORMAL_HUE_JITTER_DEGREES;
    return clampHue(roundHue(baseHue + jitter));
  });
}

function shuffleIndices(count: number, seed: number): number[] {
  const indices = Array.from({ length: count }, (_, index) => index);
  let state = seed;
  for (let i = indices.length - 1; i > 0; i -= 1) {
    state = hash32(state + i * 0x165667b1);
    const j = state % (i + 1);
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices;
}

function mapHashToHue(hash: number, minHue: number, maxHue: number): number {
  return roundHue(minHue + hashToUnit(hash) * (maxHue - minHue));
}

function hashToUnit(hash: number): number {
  return hash / 0x1_0000_0000;
}

function hash32(value: number): number {
  let hash = value >>> 0;
  hash = Math.imul(hash ^ (hash >>> 16), 0x7feb352d);
  hash = Math.imul(hash ^ (hash >>> 15), 0x846ca68b);
  return (hash ^ (hash >>> 16)) >>> 0;
}

function clampHue(hue: number): number {
  if (hue < NHOV_THEME_HUE_MIN) return NHOV_THEME_HUE_MIN;
  if (hue > NHOV_THEME_HUE_MAX) return NHOV_THEME_HUE_MAX;
  return hue;
}

function roundHue(hue: number): number {
  return Math.round(hue);
}
